<template>
  <SideSheet
    class="trader-shield"
    data-testid="trader-shield"
    position="right"
    :is-open="isOpen"
    @close="$emit('close')"
  >
    <NavBar sticky>
      <template #left>
        <BackButton icon="EF0030" @click="$emit('close')" />
      </template>
    </NavBar>
    <HelperViewContextual
      v-if="marginAllocation.isTraderShieldEnabled"
      :class="helperViewClass"
      :title="title"
      :description="description"
      icon="BC0030"
      context="positive"
    />
    <section v-else>
      <header class="trader-shield__header">
        <HeadingDisplay
          :title="traderShieldDetails.title"
          :subtitle="traderShieldDetails.subtitle"
        />
        <InitialAvatarIcon
          class="trader-shield__header-icon"
          size="medium"
          icon="BC0030"
        />
      </header>
      <BaseText size="p4" class="trader-shield__description">
        {{ traderShieldDetails.description }}
      </BaseText>
      <section class="trader-shield__content">
        <Heading :level="3">Como funciona</Heading>
        <div class="trader-shield__content-details">
          <Row
            v-for="(item, index) in traderShieldDetails.rows"
            :key="index"
            :left-title="item.title"
            :left-subtitle="item.subtitle"
            :has-line="false"
          >
            <template #icon-left>
              <InitialAvatarIcon
                class="trader-shield__content-icon"
                size="medium"
                :icon="item.icon"
              />
            </template>
          </Row>
        </div>
      </section>
    </section>
    <PasswordModal
      :is-open="isPasswordModalOpen"
      @close="isPasswordModalOpen = false"
      @confirm="confirmPassword"
    />
    <ButtonContainer class="trader-shield__button-container">
      <template #button-primary>
        <ButtonPrimary
          v-if="marginAllocation.isTraderShieldEnabled"
          label="Desativar Trader Shield"
          :loading="isLoading"
          size="large"
          @click="isPasswordModalOpen = true"
        />
        <ButtonPrimary
          v-else
          label="Ativar Trader Shield"
          size="large"
          :loading="isLoading"
          @click="handleTraderShield"
        />
      </template>
    </ButtonContainer>
  </SideSheet>
</template>

<script lang="ts" setup>
import { computed, inject, Ref, ref, watch } from 'vue';
import { removeBodyScroll } from '@/modules/common/helpers/browser';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import {
  NavBar,
  HelperViewContextual,
  HeadingDisplay,
  InitialAvatarIcon,
  Heading,
  BaseText,
  Row,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { SideSheet } from '@/modules/common/components/SideSheet';
import { BackButton } from '@/modules/common/components/buttons';
import { PasswordModal } from '@/modules/trade/components/PasswordModal';

import {
  updateTraderShield,
  IMarginAllocationV2
} from '@/modules/trade/services/margin-allocation';

interface EmitEvents {
  (event: 'close'): void;
}
const emit = defineEmits<EmitEvents>();

interface Props {
  isOpen: boolean;
  editMode: boolean;
}
const props = defineProps<Props>();

const marginAllocation = inject('marginAllocation') as Ref<IMarginAllocationV2>;
const fetchMarginAllocation = inject('fetchMarginAllocation', async () => ({}));

const isLoading = ref(false);
const isPasswordModalOpen = ref(false);

const helperViewClass = computed(() => {
  return props.editMode
    ? 'trader-shield__helper edit-mode'
    : 'trader-shield__helper';
});

const title = computed(() => {
  return props.editMode
    ? 'Para editar ou desalocar, desative o Trader Shield'
    : 'Trader Shield ativo';
});

const description = computed(() => {
  return props.editMode
    ? 'Seu limite está protegido. Desative o Trader Shield para editar ou desalocar.'
    : 'Uma camada extra de proteção para o seu patrimônio, desenvolvida pela Warren.';
});

const traderShieldDetails = computed(
  () => marginAllocation.value.traderShieldDetails
);

function confirmPassword() {
  isPasswordModalOpen.value = false;
  handleTraderShield();
}

async function handleTraderShield() {
  try {
    isLoading.value = true;

    const { isTraderShieldEnabled } = await updateTraderShield(
      marginAllocation.value!.accountId
    );

    await fetchMarginAllocation();

    notify({
      group: 'wrn-notifications',
      text: isTraderShieldEnabled
        ? 'O Trader Shield está ativo'
        : 'O Trader Shield foi desativado',
      type: NotificationTypes.success
    });
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao ativar/desativar o Trader Shield. Tente novamente em breve.',
      type: NotificationTypes.error
    });
  } finally {
    isLoading.value = false;
    emit('close');
  }
}

watch(
  () => props.isOpen,
  _isOpen => {
    removeBodyScroll();
  }
);
</script>

<style lang="less" scoped>
.trader-shield {
  height: 100vh;

  .navbar-container {
    border-bottom: none;
  }

  :deep(.panel-content.right) {
    width: 683px;
  }

  &__helper {
    :deep(.initial-avatar-icon) {
      color: @status-positive;
      background-color: @status-positive-background;
    }

    width: 320px;
    margin: 0 auto;
    height: calc(100vh - 162px);
    justify-content: center;

    &.edit-mode {
      :deep(.initial-avatar-icon) {
        color: @status-alert;
        background-color: @status-alert-background;
      }
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: @size-spacing-x800 @size-spacing-x1200 0;

    &-icon {
      background-color: @status-info-background;
      color: @status-info;
    }
  }

  &__description {
    padding: 0 @size-spacing-x1200;
    margin-top: @size-spacing-x1000;
  }

  &__content {
    padding: @size-spacing-x1000 @size-spacing-x1200;
    margin-top: @size-spacing-x1000;
    background-color: @background-tertiary;

    &-details {
      margin-top: @size-spacing-x400;

      :deep(.row-item) {
        padding: 0;
        background-color: transparent;
      }
    }

    &-icon {
      background-color: @background-secondary;
      color: @element-primary;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 683px;
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 20;
  }
}
</style>
